/**
 * Stampa della Pianificazione Auto di Cortesia.
 *
 * Meccanismo identico a WorkOrderPrintView.tsx:
 *   buildAdcHTML → Blob → URL.createObjectURL → window.open
 *
 * Orientamento A4 orizzontale (più colonne rispetto al board).
 * NON riproduce il Gantt: produce un elenco tabellare leggibile su carta.
 * Le assegnazioni vengono ordinate per targa poi per data inizio dal chiamante.
 */

import type { CarAssignmentFull } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function esc(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escOrDash(v: unknown): string {
  const s = esc(v)
  return s || '—'
}

function fmtDate(s?: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const STATO_LABEL: Record<string, string> = {
  PRENOTATA:  'Prenotata',
  IN_CORSO:   'In corso',
  COMPLETATA: 'Completata',
  ANNULLATA:  'Annullata',
}

// ── HTML builder ──────────────────────────────────────────────────────────────

function buildAdcHTML(periodo: string, assegnazioni: CarAssignmentFull[]): string {
  const printedAt = new Date().toLocaleString('it-IT')
  const n = assegnazioni.length
  const nLabel = n === 1 ? '1 assegnazione' : `${n} assegnazioni`

  const rowsHTML = n === 0
    ? `<tr><td colspan="6" style="text-align:center;color:#999;font-style:italic;padding:20px 0">
         Nessuna assegnazione per il periodo selezionato
       </td></tr>`
    : assegnazioni.map(a => {
        const statoLabel = STATO_LABEL[a.stato] ?? a.stato
        return `
<tr class="adc-row">
  <td class="cell-auto"><strong>${escOrDash(a.courtesy_car_targa)}</strong>${a.courtesy_car_modello ? `<br><span class="sub">${esc(a.courtesy_car_modello)}</span>` : ''}</td>
  <td class="cell-cliente">${escOrDash(a.customer_nome)}</td>
  <td class="cell-date">${fmtDate(a.data_inizio)}</td>
  <td class="cell-date">${fmtDate(a.data_fine_prevista)}</td>
  <td class="cell-scheda">${escOrDash(a.work_order_codice)}</td>
  <td class="cell-stato stato-${esc(a.stato)}">${esc(statoLabel)}</td>
</tr>`
      }).join('')

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8">
  <title>Auto di Cortesia — ${esc(periodo)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; background: #f0f0f0; color: #111; }

    /* ── Toolbar ── */
    #print-toolbar {
      position: fixed; top: 0; left: 0; right: 0;
      background: #1677ff; color: #fff;
      display: flex; align-items: center;
      padding: 10px 20px; gap: 12px;
      z-index: 9999; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    #print-toolbar span { font-size: 14px; font-weight: bold; flex: 1; }
    #print-toolbar button {
      padding: 6px 20px; border: none; border-radius: 4px;
      cursor: pointer; font-size: 13px; font-weight: bold;
    }
    #btn-print { background: #fff; color: #1677ff; }
    #btn-close  { background: rgba(255,255,255,0.2); color: #fff; border: 1px solid rgba(255,255,255,0.5) !important; }
    #btn-print:hover { background: #e6f7ff; }
    #btn-close:hover  { background: rgba(255,255,255,0.3); }

    /* ── Wrapper ── */
    #page-wrapper { padding: 60px 20px 20px 20px; min-height: 100vh; }
    .doc-page {
      background: #fff;
      width: 297mm;
      min-height: 210mm;
      margin: 0 auto;
      padding: 10mm 12mm 14mm 12mm;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    }

    @page { size: A4 landscape; margin: 10mm 12mm; }
    @media print {
      #print-toolbar { display: none !important; }
      #page-wrapper  { padding: 0; background: none; }
      body           { background: none; }
      .doc-page      { box-shadow: none; width: 100%; min-height: unset; padding: 0; margin: 0; }
    }

    /* ── Header ── */
    .doc-label { font-size: 9px; color: #777; text-transform: uppercase; letter-spacing: 0.5px; }
    .doc-title { font-size: 20px; font-weight: bold; margin: 2px 0 3px 0; }
    .doc-period{ font-size: 14px; font-weight: bold; color: #333; }
    .doc-meta  { font-size: 10px; color: #666; margin-top: 3px; }
    .divider   { border: none; border-top: 2px solid #111; margin: 8px 0 10px 0; }

    /* ── Tabella dati ── */
    table.adc { width: 100%; border-collapse: collapse; }
    table.adc thead th {
      font-size: 9px; font-weight: bold; text-transform: uppercase;
      letter-spacing: 0.4px; color: #444;
      border-top: 2px solid #111; border-bottom: 1px solid #555;
      padding: 4px 6px; background: none; text-align: left;
    }
    tr.adc-row { break-inside: avoid; page-break-inside: avoid; }
    tr.adc-row td {
      border-bottom: 1px solid #ccc;
      vertical-align: top;
      padding: 7px 6px;
    }

    .cell-auto    { width: 48mm; }
    .cell-cliente { width: 48mm; }
    .cell-date    { width: 30mm; font-size: 10px; }
    .cell-scheda  { width: 30mm; }
    .cell-stato   { width: 24mm; font-size: 10px; text-align: center; }

    .sub { font-size: 9px; color: #666; }
    .stato-IN_CORSO  { font-weight: bold; }
    .stato-ANNULLATA { color: #777; }

    /* ── Footer ── */
    .print-footer {
      margin-top: 8px; font-size: 9px; color: #aaa;
      text-align: center; border-top: 1px solid #e8e8e8; padding-top: 4px;
    }
  </style>
</head>
<body>

<div id="print-toolbar">
  <span>Anteprima Stampa — Pianificazione Auto di Cortesia · ${esc(periodo)}</span>
  <button id="btn-print" onclick="window.print()">🖨 Stampa</button>
  <button id="btn-close"  onclick="window.close()">✕ Chiudi</button>
</div>

<div id="page-wrapper">
<div class="doc-page">

  <div>
    <div class="doc-label">Pianificazione</div>
    <div class="doc-title">Auto di Cortesia</div>
    <div class="doc-period">${esc(periodo)}</div>
    <div class="doc-meta">${nLabel}</div>
  </div>

  <hr class="divider">

  <table class="adc">
    <thead>
      <tr>
        <th>Auto</th>
        <th>Cliente</th>
        <th>Dal</th>
        <th>Al</th>
        <th>Scheda</th>
        <th>Stato</th>
      </tr>
    </thead>
    <tbody>${rowsHTML}</tbody>
  </table>

  <div class="print-footer">
    Stampato il ${printedAt}
  </div>

</div>
</div>
</body>
</html>`
}

// ── Funzione pubblica ─────────────────────────────────────────────────────────

export function printAdcPlanning(periodo: string, assegnazioni: CarAssignmentFull[]): void {
  const html = buildAdcHTML(periodo, assegnazioni)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const win = window.open(url, '_blank', 'width=1100,height=800')
  if (!win) {
    URL.revokeObjectURL(url)
    alert("Il browser ha bloccato l'apertura della finestra di anteprima. Abilita i popup per questo sito.")
    return
  }
  win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true })
  win.focus()
}
